// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-RPT-2 phase 2 (UI) — the AI report builder you can actually use.
 *
 * Select a pharmacy, describe a report, the AI reads THAT pharmacy's real schema and writes SQL,
 * you run it and see real rows, edit the SQL by hand or ask the AI to fix it. This is the
 * authoring experience that makes phase 1's endpoints usable; publish/masking/roles are phase 3.
 *
 * Sprint A adds authoring niceties: pick the AI model, see the AI cost of each generation, page
 * through results, and export the current result to CSV.
 */
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Play, Wand2, AlertTriangle, Database, ChevronLeft, ChevronRight, Download, History, Gauge, UploadCloud } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const PAGE_SIZE = 100

export default function ReportBuilderSqlPage() {
  const navigate = useNavigate()
  const [pharmacies, setPharmacies] = useState<api.PharmacyListItem[]>([])
  const [pharmacyId, setPharmacyId] = useState('')
  const [models, setModels] = useState<api.ReportAiModel[]>([])
  const [modelId, setModelId] = useState('')
  const [prompt, setPrompt] = useState('')
  const [thinking, setThinking] = useState(false)
  const [explanation, setExplanation] = useState('')
  const [sql, setSql] = useState('')
  const [sqlValid, setSqlValid] = useState<boolean | null>(null)
  const [sqlReason, setSqlReason] = useState<string | null>(null)
  const [schemaInfo, setSchemaInfo] = useState<{ tables: number; columns: number } | null>(null)
  const [aiUsage, setAiUsage] = useState<api.ReportAiSqlResponse['usage'] | null>(null)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<api.ReportRunSqlResponse | null>(null)
  const [page, setPage] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [lastRunError, setLastRunError] = useState<string | null>(null)
  const [history, setHistory] = useState<HistItem[]>([])
  const [impact, setImpact] = useState<api.ReportImpact | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkedSql, setCheckedSql] = useState('')
  const [heavyConfirmed, setHeavyConfirmed] = useState(false)
  const [publishName, setPublishName] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [publishMsg, setPublishMsg] = useState<string | null>(null)
  const [published, setPublished] = useState<api.PublishedReport[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)

  useEffect(() => {
    api.listPharmacies().then(r => setPharmacies(r.items ?? [])).catch(() => setPharmacies([]))
    api.getReportAiModels().then(r => { setModels(r.models ?? []); setModelId(r.default) }).catch(() => setModels([]))
  }, [])

  // Every generated/run query is kept per-customer in the browser so you can reload one instead of
  // paying the AI to regenerate it. No PHI — it's SQL text. Server-side saved reports come with publish.
  function pushHistory(q: string) {
    const s = q.trim()
    if (!s || !pharmacyId) return
    setHistory(prev => {
      const next = [{ sql: s, ts: Date.now() }, ...prev.filter(h => h.sql !== s)].slice(0, 50)
      saveHistory(pharmacyId, next)
      return next
    })
  }

  const selectedName = pharmacies.find(p => p.id === pharmacyId)?.name
  const modelLabel = models.find(m => m.id === modelId)?.label ?? modelId

  async function describe(fixError?: string) {
    if (!pharmacyId || (!prompt.trim() && !fixError)) return
    setThinking(true); setError(null)
    try {
      const r = await api.reportAiSql({
        pharmacyId,
        model: modelId || undefined,
        messages: [{ role: 'user', content: fixError ? 'Fix the query.' : prompt.trim() }],
        currentSql: sql || undefined,
        lastError: fixError,
      })
      setExplanation(r.explanation)
      if (r.sql) { setSql(r.sql); pushHistory(r.sql) }
      setSqlValid(r.sql_valid)
      setSqlReason(r.sql_reason)
      setSchemaInfo({ tables: r.schema_tables, columns: r.schema_columns })
      setAiUsage(r.usage ?? null)
    } catch (e: any) {
      setError(friendly(e))
    } finally { setThinking(false) }
  }

  async function run() {
    if (!pharmacyId || !sql.trim()) return
    setRunning(true); setError(null); setResult(null); setPage(0)
    pushHistory(sql)
    try {
      const r = await api.reportRunSql({ pharmacyId, sql })
      setResult(r)
    } catch (e: any) {
      const msg = friendly(e)
      setError(msg)
      // A run error is exactly what "AI, fix it" needs — stash it so the button can pass it back.
      setLastRunError(e?.body?.message || msg)
    } finally { setRunning(false) }
  }

  async function checkImpact() {
    if (!pharmacyId || !sql.trim()) return
    setChecking(true); setError(null); setHeavyConfirmed(false)
    try {
      const im = await api.reportEstimate(pharmacyId, sql)
      setImpact(im); setCheckedSql(sql)
    } catch (e: any) {
      setError(friendly(e)); setImpact(null); setCheckedSql('')
    } finally { setChecking(false) }
  }

  async function publish() {
    if (!pharmacyId || !sql.trim() || !publishName.trim()) return
    setPublishing(true); setPublishMsg(null); setError(null)
    try {
      if (editingId) {
        await api.editPublishedReport(editingId, publishName.trim(), sql)
        setPublishMsg(`Updated & republished "${publishName.trim()}".`)
        setEditingId(null)
      } else {
        await api.reportPublish(pharmacyId, publishName.trim(), sql)
        setPublishMsg(`Published "${publishName.trim()}" to ${selectedName}.`)
      }
      setPublishName('')
      const r = await api.listPublishedReports(pharmacyId)
      setPublished(r.items ?? [])
    } catch (e: any) { setError(friendly(e)) }
    finally { setPublishing(false) }
  }

  async function loadForEdit(id: string) {
    setError(null)
    try {
      const r = await api.getPublishedReport(id)
      setSql(r.sql); setPublishName(r.name); setEditingId(id)
      setSqlValid(null); setImpact(null); setCheckedSql(''); setResult(null)
      setPublishMsg(`Editing "${r.name}" — change the query above, check impact, then Update & republish.`)
    } catch (e: any) { setError(friendly(e)) }
  }

  async function recall(id: string) {
    setError(null)
    try {
      await api.recallPublishedReport(id)
      const r = await api.listPublishedReports(pharmacyId)
      setPublished(r.items ?? [])
    } catch (e: any) { setError(friendly(e)) }
  }

  async function exportCsv() {
    if (!pharmacyId || !sql.trim()) return
    setError(null)
    try {
      const { blob, filename } = await api.exportBuilderReportCsv(pharmacyId, sql)
      saveBlob(blob, filename)
    } catch (e: any) { setError(friendly(e)) }
  }

  // D-UI-2: a funnel on every result column. It filters the whole fetched result; the page is
  // sliced from what passes, and a funnel change returns to the first page.
  const resultColumns = result?.columns
  const resultFunnels = useMemo<FunnelColumn<unknown[]>[]>(() => (resultColumns ?? []).map((c, i) => ({
    key: `${i}:${c}`, label: c,
    value: (row: unknown[]) => { const v = row[i]; return v !== null && typeof v === 'object' ? JSON.stringify(v) : v },
  })), [resultColumns])
  const fx = useTableFunnels(result?.rows, resultFunnels)
  useEffect(() => { setPage(0) }, [fx.sig])

  const totalRows = fx.rows.length
  const pageCount = Math.max(1, Math.ceil(totalRows / PAGE_SIZE))
  const pageStart = page * PAGE_SIZE
  const pageRows = fx.rows.slice(pageStart, pageStart + PAGE_SIZE)

  // The impact check is mandatory before Run: you must have checked the CURRENT SQL, and a query the
  // check flags as heavy needs an explicit "run anyway" before it can execute.
  const checked = checkedSql !== '' && checkedSql === sql
  const runBlocked = !checked || (!!impact?.heavy && !heavyConfirmed)

  return (
    <div style={{ padding: '18px 22px', maxWidth: 1200, margin: '0 auto' }}>
      <button onClick={() => navigate('/admin/reports')} style={backLink}>
        <ChevronLeft size={14} aria-hidden="true" /> Classic report builder
      </button>

      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 21, margin: '4px 0 4px' }}>
        <Sparkles size={19} aria-hidden="true" /> AI Report Builder
      </h1>
      <p style={{ margin: '0 0 6px', color: 'var(--color-text-mute)', fontSize: 13 }}>
        Pick a customer, describe the report in plain language. The AI reads that pharmacy's real
        database schema and writes the query — you run it and see the rows before anyone else does.
      </p>
      <p style={{ margin: '0 0 16px' }}>
        <button onClick={() => navigate('/admin/reports-ai-prompt')} style={linkBtn}>Edit AI prompt →</button>
      </p>

      {/* Step 1 — pharmacy + model. SearchableSelect per the project standard (Gate 19). */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={fieldLabel}>Customer
          <div style={{ width: 420, maxWidth: '100%', marginTop: 4 }}>
            <SearchableSelect
              value={pharmacyId}
              onChange={v => {
                setPharmacyId(v); setResult(null); setSchemaInfo(null); setAiUsage(null); setHistory(loadHistory(v))
                setImpact(null); setCheckedSql(''); setPublishMsg(null)
                api.listPublishedReports(v).then(r => setPublished(r.items ?? [])).catch(() => setPublished([]))
              }}
              ariaLabel="Customer"
              placeholder="Select a pharmacy…"
              options={pharmacies.map(p => ({ value: p.id, label: p.name }))}
            />
          </div>
        </div>
        <div style={fieldLabel}>AI model
          <div style={{ width: 300, maxWidth: '100%', marginTop: 4 }}>
            <SearchableSelect
              value={modelId}
              onChange={setModelId}
              ariaLabel="AI model"
              placeholder="Model…"
              options={models.map(m => ({ value: m.id, label: m.label }))}
            />
          </div>
        </div>
      </div>

      {schemaInfo && (
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Database size={13} aria-hidden="true" />
          The AI is reading <strong>{selectedName}</strong>'s live schema — {schemaInfo.tables} tables, {schemaInfo.columns} columns.
        </div>
      )}

      {/* Step 2 — describe */}
      <label style={{ ...fieldLabel, marginTop: 16 }}>Describe the report
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={2}
          placeholder="e.g. count of prescriptions by drug name, this year"
          disabled={!pharmacyId}
          style={{ ...selectStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </label>
      <button onClick={() => describe()} disabled={!pharmacyId || !prompt.trim() || thinking} style={primaryBtn}>
        <Sparkles size={14} aria-hidden="true" /> {thinking ? 'Writing the query…' : 'Ask the AI'}
      </button>

      {error && (
        <div style={errBox}><AlertTriangle size={15} aria-hidden="true" style={{ flexShrink: 0 }} /> {error}</div>
      )}

      {/* History — reload a past query instead of paying the AI to regenerate it. Visible whenever a
          customer is selected (its saved queries load on select), so it is always findable. */}
      {pharmacyId && (
        <details style={{ marginTop: 16 }} open={history.length > 0}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <History size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />
            History ({history.length}) — reload a past query, no AI cost
          </summary>
          {history.length === 0 ? (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-mute)' }}>
              No saved queries yet for {selectedName}. Every query you generate or run is saved here (per customer, in this browser).
            </div>
          ) : (
            <>
              <div style={{ marginTop: 6, border: '1px solid var(--color-border)', borderRadius: 8, maxHeight: 240, overflowY: 'auto' }}>
                {history.map((h, i) => (
                  <button key={i} onClick={() => { setSql(h.sql); setSqlValid(null); setError(null) }} style={histItem} title={h.sql}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5 }}>{oneLine(h.sql).slice(0, 110)}</span>
                    <span style={{ color: 'var(--color-text-mute)', fontSize: 11, flexShrink: 0, marginLeft: 8 }}>{relTime(h.ts)}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => { setHistory([]); if (pharmacyId) saveHistory(pharmacyId, []) }} style={{ ...linkBtn, marginTop: 6, fontSize: 11.5 }}>Clear history</button>
            </>
          )}
        </details>
      )}

      {/* Step 3 — SQL: always available. Write your own query, or let the AI fill it. */}
      <section style={{ marginTop: 18 }}>
          {explanation && <p style={{ fontSize: 13.5, margin: '0 0 8px' }}>{explanation}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>Query</strong>
            <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>— write your own or use the AI above</span>
            {sqlValid === false && (
              <span style={{ fontSize: 12, color: 'var(--color-danger, #b3261e)' }}>
                <AlertTriangle size={12} aria-hidden="true" style={{ verticalAlign: -2 }} /> {sqlReason}
              </span>
            )}
            {sqlValid === true && <span style={{ fontSize: 12, color: 'var(--color-primary, #0d9488)' }}>✓ valid</span>}
            {aiUsage && (
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)', marginLeft: 'auto' }}
                title="Token usage and estimated cost of this AI generation. An estimate for visibility, not a bill.">
                AI: {modelLabel} · {fmtInt(aiUsage.input_tokens)} in / {fmtInt(aiUsage.output_tokens)} out · {fmtCost(aiUsage.cost_usd)}
              </span>
            )}
          </div>
          <textarea value={sql} onChange={e => { setSql(e.target.value); setSqlValid(null) }} rows={10}
            spellCheck={false}
            style={{ ...selectStyle, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, resize: 'vertical', lineHeight: 1.5 }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={checkImpact} disabled={checking || !sql.trim() || !pharmacyId} style={ghostBtn}>
              <Gauge size={14} aria-hidden="true" /> {checking ? 'Checking…' : 'Check impact'}
            </button>
            <button onClick={run} disabled={running || !sql.trim() || runBlocked} style={primaryBtn}
              title={runBlocked ? 'Check impact before running' : undefined}>
              <Play size={14} aria-hidden="true" /> {running ? 'Running…' : 'Run against live data'}
            </button>
            <button onClick={() => describe(lastRunError || 'The query needs adjusting.')} disabled={thinking} style={ghostBtn}>
              <Wand2 size={14} aria-hidden="true" /> AI, fix it
            </button>
            {!checked && sql.trim() && (
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Check impact before running.</span>
            )}
          </div>

          {/* Mandatory pre-run impact check — complexity score + DB impact */}
          {impact && checked && (
            <div style={{
              marginTop: 10, padding: '9px 12px', borderRadius: 7, fontSize: 12.5,
              border: '1px solid ' + (impact.heavy ? 'var(--color-danger-border, #f0c9c9)' : 'var(--color-border)'),
              background: impact.heavy ? 'var(--color-danger-bg, #fdf2f2)' : 'var(--color-surface-alt, #f6f8fa)',
              color: impact.heavy ? 'var(--color-danger, #b3261e)' : 'var(--color-text)',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                {impact.heavy ? '⚠ Heavy query' : '✓ Impact check'}
                {impact.showplan_available && impact.cost != null && ` · complexity ${impact.cost < 1 ? '<1' : impact.cost.toFixed(1)}`}
              </div>
              <div>{impact.summary}</div>
              {impact.tables.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--color-text-mute)' }}>
                  Tables touched: {impact.tables.map(t => `${t.name} (${t.rows.toLocaleString()} rows)`).join(', ')}
                </div>
              )}
              {impact.heavy && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontWeight: 600 }}>
                  <input type="checkbox" checked={heavyConfirmed} onChange={e => setHeavyConfirmed(e.target.checked)} />
                  I understand this may be slow — run anyway
                </label>
              )}
            </div>
          )}

          {/* Publish to the pharmacy */}
          {pharmacyId && sql.trim() && sqlValid !== false && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {editingId ? 'Update the published report' : `Publish this report to ${selectedName}`}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={publishName} onChange={e => setPublishName(e.target.value)} placeholder="Report name (e.g. Shipped Revenue)"
                  style={{ ...selectStyle, width: 340, maxWidth: '100%', marginTop: 0 }} />
                <button onClick={publish} disabled={publishing || !publishName.trim()} style={primaryBtn}>
                  <UploadCloud size={14} aria-hidden="true" /> {publishing ? 'Saving…' : editingId ? 'Update & republish' : 'Publish to pharmacy'}
                </button>
                {editingId && (
                  <button onClick={() => { setEditingId(null); setPublishName(''); setPublishMsg(null) }} style={ghostBtn}>Cancel edit</button>
                )}
              </div>
              {publishMsg && <div style={{ fontSize: 12.5, color: 'var(--color-primary, #0d9488)', marginTop: 6 }}>{publishMsg}</div>}
            </div>
          )}

          {/* Reports already published to this pharmacy — recall or load to edit */}
          {published.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Published to {selectedName} ({published.filter(p => p.is_active).length} live)
              </summary>
              <div style={{ marginTop: 6, border: '1px solid var(--color-border)', borderRadius: 8 }}>
                {published.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--color-border)' }}>
                    <span style={{ fontSize: 12.5, textDecoration: p.is_active ? 'none' : 'line-through', color: p.is_active ? 'var(--color-text)' : 'var(--color-text-mute)' }}>
                      {p.name}{!p.is_active && ' (recalled)'}
                    </span>
                    {p.is_active && (
                      <span style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                        <button onClick={() => loadForEdit(p.id)} style={linkBtn}>Edit</button>
                        <button onClick={() => recall(p.id)} style={{ ...linkBtn, color: 'var(--color-danger, #b3261e)' }}>Recall</button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
      </section>

      {/* Step 4 — results */}
      {result && (
        <section style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap', fontSize: 13 }}>
            <span>
              <strong>{result.row_count.toLocaleString()}</strong> row{result.row_count === 1 ? '' : 's'} · {result.elapsed_ms} ms
            </span>
            {result.estimated_cost < 0 ? (
              <span style={{ color: 'var(--color-text-mute)' }}
                title="The query's cost couldn't be estimated — the SHOWPLAN permission isn't granted on this database, so the cost gate is inactive. The query ran under the 30s timeout instead.">
                complexity n/a
              </span>
            ) : (
              <span style={{ color: 'var(--color-text-mute)' }}>
                complexity {result.estimated_cost < 1 ? '<1' : result.estimated_cost.toFixed(1)}
                {result.estimated_cost >= 100 && <span style={{ color: '#b45309' }}> (heavy)</span>}
              </span>
            )}
            {result.truncated && (
              <span style={{ color: '#b45309' }}>
                <AlertTriangle size={13} aria-hidden="true" style={{ verticalAlign: -2 }} /> on-screen preview capped at {result.row_cap.toLocaleString()} — Export CSV pulls the complete set
              </span>
            )}
            {result.row_count > 0 && (
              <button onClick={exportCsv} style={{ ...ghostBtn, marginTop: 0, marginLeft: 'auto', padding: '5px 10px' }}>
                <Download size={13} aria-hidden="true" /> Export CSV (all rows)
              </button>
            )}
          </div>
          {fx.chips}
          <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
            <table {...fx.tableProps} style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface-alt, #f6f8fa)' }}>
                  {result.columns.map((c, i) => <th key={c} style={cellHead}>{c} {fx.funnel(`${i}:${c}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, ri) => (
                  <tr key={pageStart + ri} style={{ borderTop: '1px solid var(--color-border)' }}>
                    {row.map((v, ci) => <td key={ci} style={cell}>{fmt(v)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination over the fetched rows */}
          {totalRows > PAGE_SIZE && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 12.5 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={pageBtn}>
                <ChevronLeft size={13} aria-hidden="true" /> Prev
              </button>
              <span style={{ color: 'var(--color-text-mute)' }}>
                {(pageStart + 1).toLocaleString()}–{Math.min(pageStart + PAGE_SIZE, totalRows).toLocaleString()} of {totalRows.toLocaleString()}
              </span>
              <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} style={pageBtn}>
                Next <ChevronRight size={13} aria-hidden="true" />
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// Per-customer query history, kept in the browser so a good query can be reloaded instead of paying
// the AI to regenerate it. SQL text only — never PHI. try/catch everywhere: localStorage can throw
// (private mode) or be absent, and history is a convenience, never load-bearing.
type HistItem = { sql: string; ts: number }
function histKey(pid: string): string { return `owlrx_report_sql_history_${pid}` }
function loadHistory(pid: string): HistItem[] {
  if (!pid) return []
  try {
    const raw = localStorage.getItem(histKey(pid))
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter((h: any) => typeof h?.sql === 'string') : []
  } catch { return [] }
}
function saveHistory(pid: string, items: HistItem[]): void {
  try { localStorage.setItem(histKey(pid), JSON.stringify(items.slice(0, 50))) } catch { /* private mode / quota — history is optional */ }
}
function oneLine(sql: string): string { return sql.replace(/\s+/g, ' ').trim() }
function relTime(ts: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function fmtInt(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString()
}

function fmtCost(c: number | null | undefined): string {
  if (c === null || c === undefined) return 'cost n/a'
  // Sub-cent generations are the norm; show enough precision to be meaningful.
  return c < 0.01 ? `~$${c.toFixed(4)}` : `~$${c.toFixed(2)}`
}

// The Export CSV button pulls the COMPLETE result server-side (not the on-screen 5,000-row preview),
// so a revenue report ties to the books. This just saves the returned blob.
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function friendly(e: any): string {
  const body = e?.body
  if (body?.error === 'ai_not_configured') return 'The report-builder AI key is not set (Admin → Report builder → AI assist key).'
  if (body?.error === 'tenant_catalog_unavailable') return "This environment can't resolve customer databases, so the query can't run."
  if (body?.error === 'customer_not_isolated') return 'This customer does not have its own database yet.'
  if (body?.error === 'too_expensive') return body.message
  if (body?.error === 'query_timeout') return body.message
  if (body?.error === 'invalid_sql' || body?.error === 'sql_error') return body.message
  return body?.message || e?.message || 'Something went wrong.'
}

const backLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', padding: 0, marginBottom: 6, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-mute)' }
const linkBtn: React.CSSProperties = { border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary, #0d9488)' }
const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }
const selectStyle: React.CSSProperties = { display: 'block', width: '100%', maxWidth: 640, marginTop: 4, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 7, font: 'inherit', fontSize: 13, background: 'var(--color-surface)' }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 14px', border: 'none', borderRadius: 7, background: 'var(--color-primary, #0d9488)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { ...primaryBtn, background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)' }
const pageBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '5px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const histItem: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', font: 'inherit', color: 'var(--color-text)' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: '9px 12px', border: '1px solid var(--color-danger-border, #f0c9c9)', background: 'var(--color-danger-bg, #fdf2f2)', borderRadius: 7, fontSize: 12.5, color: 'var(--color-danger, #b3261e)' }
const cellHead: React.CSSProperties = { textAlign: 'left', padding: '7px 10px', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '2px solid var(--color-border)' }
const cell: React.CSSProperties = { padding: '6px 10px', whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }
